import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';

const MAX_ROWS = 500;
const MAX_COLS = 80;

/**
 * Excel / CSV 表格预览（SheetJS xlsx）。
 */
export default function PlatformTutorialSpreadsheetPreview({
  url = '',
  fileName = '',
  onDownload,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workbook, setWorkbook] = useState(null);
  const [sheetName, setSheetName] = useState('');
  const [matrix, setMatrix] = useState({ rows: [], truncated: false });

  useEffect(() => {
    let alive = true;
    const run = async () => {
      setLoading(true);
      setError('');
      setWorkbook(null);
      setSheetName('');
      setMatrix({ rows: [], truncated: false });
      if (!url) return;
      try {
        const XLSX = await import('xlsx');
        const res = await fetch(url);
        if (!res.ok) throw new Error(`加载失败（${res.status}）`);
        const buffer = await res.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
        if (!alive) return;
        if (!wb.SheetNames?.length) throw new Error('表格为空');
        setWorkbook(wb);
        setSheetName(wb.SheetNames[0]);
      } catch (e) {
        if (alive) setError(e?.message || '表格预览失败');
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [url]);

  useEffect(() => {
    let alive = true;
    const build = async () => {
      if (!workbook || !sheetName) {
        setMatrix({ rows: [], truncated: false });
        return;
      }
      const XLSX = await import('xlsx');
      const sheet = workbook.Sheets[sheetName];
      if (!sheet) {
        setMatrix({ rows: [], truncated: false });
        return;
      }
      const raw = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: '',
        raw: false,
      });
      const truncated = raw.length > MAX_ROWS || raw.some((r) => (r?.length || 0) > MAX_COLS);
      const rows = raw.slice(0, MAX_ROWS).map((row) => {
        const cells = Array.isArray(row) ? row.slice(0, MAX_COLS) : [];
        return cells.map((cell) => (cell == null ? '' : String(cell)));
      });
      if (alive) setMatrix({ rows, truncated });
    };
    build();
    return () => {
      alive = false;
    };
  }, [workbook, sheetName]);

  if (loading) return <div className="pt-preview__empty">表格加载中…</div>;
  if (error) {
    return (
      <div className="pt-preview__fallback">
        <p>{error}</p>
        {onDownload ? (
          <button type="button" className="pt-preview__cta" onClick={onDownload}>
            <Download size={15} aria-hidden />
            下载文件
          </button>
        ) : null}
      </div>
    );
  }

  const sheets = workbook?.SheetNames || [];
  const colCount = Math.max(1, ...matrix.rows.map((r) => r.length), 1);

  return (
    <div className="pt-preview__sheet">
      {sheets.length > 1 ? (
        <div className="pt-preview__sheet-tabs" role="tablist" aria-label="工作表">
          {sheets.map((name) => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={name === sheetName}
              className={`pt-preview__sheet-tab${name === sheetName ? ' is-active' : ''}`}
              onClick={() => setSheetName(name)}
            >
              {name}
            </button>
          ))}
        </div>
      ) : null}
      <div className="pt-preview__sheet-scroll">
        {matrix.truncated ? (
          <div className="pt-preview__sheet-hint">
            仅预览前 {MAX_ROWS} 行 / {MAX_COLS} 列
            {fileName ? `（${fileName}）` : ''}
          </div>
        ) : null}
        <table className="pt-preview__sheet-table">
          <tbody>
            {matrix.rows.length === 0 ? (
              <tr>
                <td colSpan={colCount}>（空表）</td>
              </tr>
            ) : (
              matrix.rows.map((row, ri) => (
                <tr key={`r-${ri}`}>
                  <th scope="row" className="pt-preview__sheet-rownum">{ri + 1}</th>
                  {Array.from({ length: colCount }, (_, ci) => (
                    <td key={`c-${ri}-${ci}`}>{row[ci] || ''}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
